import { useEffect, useRef } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { billing } from '@/services/billing';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { hasTaxableAddress, TAX_DEBOUNCED_FIELDS, TAX_LOOKUP_DEBOUNCE_MS } from '../utils/tax-lookup-rules';
import { useAbortableTaxLookup } from './use-abortable-tax-lookup';
import { sleep } from '@/utils/sleep';

/** The four address fields a tax-rate request is made of. */
type TaxLookupTarget = Pick<CreditCardFormAddress, 'country' | 'state' | 'zipCode' | 'addressLine1'>;

function taxLookupTarget(address: CreditCardFormAddress): TaxLookupTarget {
  return {
    country: address.country,
    state: address.state,
    zipCode: address.zipCode,
    addressLine1: address.addressLine1,
  };
}

// Whether the move to this target came from typing, so its request waits out the debounce first. The
// first lookup of the form has no previous target: that address was chosen, not typed, so it goes now.
function isTypedChange(previous: TaxLookupTarget | null, next: TaxLookupTarget): boolean {
  return previous != null && TAX_DEBOUNCED_FIELDS.some((field) => previous[field] !== next[field]);
}

/**
 * Drives the billing tax-rate store from the current address, returning the rate fraction and whether a
 * lookup is in flight. Tax applies on both the new-card and saved-card paths (the saved-card path uses
 * the account address) so the "including tax" total is always accurate.
 *
 * The invariant: a rate is reported only for the address on screen. So the query is keyed on the live
 * address — never on a delayed copy of it — and the debounce for typed fields runs inside the query
 * function rather than before it. That is what lets `isFetching` also cover the "still typing, nothing
 * sent yet" window: submit is blocked from the keystroke, not just from the request. Keying on a delayed
 * address instead left up to a debounce of the live address priced with the previous address's tax, and
 * submit open, because both the completeness gate and the key still described the old address.
 */
/** One place for the key shape, so the abort site removes exactly the entry the run is registered under. */
function creditsPurchaseTaxRateQueryKey(target: TaxLookupTarget): QueryKey {
  return ['creditsPurchaseTaxRate', target.country, target.state, target.zipCode, target.addressLine1];
}

export type CreditsPurchaseTaxRate = {
  /** The rate fraction for the address on screen, or undefined when nothing prices it. */
  rate: number | undefined;
  isFetching: boolean;
};

export function useCreditsPurchaseTaxRate(address: CreditCardFormAddress): CreditsPurchaseTaxRate {
  const taxRate = useStore(billing.taxRate);
  const target = taxLookupTarget(address);
  const isComplete = hasTaxableAddress(address);

  // Supersedes the lookup started for the previous address. `updateTaxRate` writes the rate inside the
  // billing service and `enqueueTask` runs serially with no cancellation, so aborting the signal is the
  // only thing that stops an older run from writing over a rate a newer address has already cleared or
  // replaced. React Query cancels neither a running `queryFn` whose key changed nor one whose query has
  // become disabled, so without this the in-flight lookup landed after `resetTaxRate` and priced an
  // untaxable address with the previous address's tax — with submit no longer blocked, because
  // `isFetching` below is gated on `isComplete`. Same guard, and the same `signal`, as checkout's
  // `useTaxRateRefresh`.
  // Also drops the superseded run's cache entry, so an address returning to it starts a fresh lookup
  // instead of being deduped onto a run that can no longer write a rate. See `useAbortableTaxLookup`.
  const lookup = useAbortableTaxLookup();
  // The target the last lookup was started for, which is what says whether this one was typed. Held in a
  // ref because it decides how a request runs, not what the form renders.
  const lastTargetRef = useRef<TaxLookupTarget | null>(null);

  const queryKey = creditsPurchaseTaxRateQueryKey(target);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Abort from in here rather than from a render or an effect: a change that resolves to the key
      // already in flight never reaches this point (React Query dedupes it), and it must not have the
      // lookup it is waiting on cancelled out from under it.
      const signal = lookup.begin(queryKey);

      const isTyped = isTypedChange(lastTargetRef.current, target);
      lastTargetRef.current = target;
      if (isTyped) {
        await sleep(TAX_LOOKUP_DEBOUNCE_MS, signal);
        if (signal.aborted) {
          return null;
        }
      }

      await billing.updateTaxRate.perform({ ...target, signal });
      // Read the store rather than the awaited value: a superseded run skips its write, so the newer
      // run's rate is what belongs to the address on screen. The caller reads the store too.
      return billing.taxRate.get()?.rate ?? null;
    },
    enabled: isComplete,
    // A rate belongs to one address and one edit: never serve it from cache, and never keep it once the
    // address has moved on.
    staleTime: 0,
    gcTime: 0,
  });

  // Clear any stored rate once the address is no longer complete, and on unmount. Abort first, or the
  // lookup this reset supersedes writes its rate back afterwards.
  useEffect(() => {
    if (!isComplete) {
      lookup.abort();
      billing.resetTaxRate();
    }
  }, [isComplete, lookup]);
  useEffect(
    () => () => {
      lookup.abort();
      billing.resetTaxRate();
    },
    [lookup],
  );

  // Report no rate for an address we do not price, without waiting for the reset effect to run: the
  // totals must never show tax the address on screen cannot carry, not even for one paint.
  return { rate: isComplete ? taxRate?.rate : undefined, isFetching: isComplete && query.isFetching };
}
